import React from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { currentLang } from "../i18n";

// Canonical production URL. Update here if the apex domain changes.
const SITE_URL = "https://lt.plus";

// All locales the site is shipped in. Used to emit hreflang alternates.
const LOCALES = ["en", "de", "fr", "it", "rm"];

// Default OG image. 1:1 logo isn't ideal for OG's 1.91:1 preview, but it still renders.
const OG_IMAGE = `${SITE_URL}/LT+_black.png`;

/**
 * Per-page SEO head tags. Reads copy from the `seo.pages.<page>` namespace in
 * the active i18n bundle and falls back to the `seo.default*` keys when a page
 * doesn't override. Canonical and hreflang links always point to the current path.
 * `noindex` marks a sheet that must stay out of the index (the 404).
 */
export default function Seo({ page = "home", path, noindex = false }) {
  const { t, i18n } = useTranslation();
  const lang = currentLang(i18n);

  const pagePrefix = `seo.pages.${page}`;
  const title = t(`${pagePrefix}.title`, {
    defaultValue: t("seo.defaultTitle"),
  });
  const description = t(`${pagePrefix}.description`, {
    defaultValue: t("seo.defaultDesc"),
  });
  const siteName = t("seo.siteName", { defaultValue: "LTplus AG" });
  const pathname =
    path ?? (typeof window !== "undefined" ? window.location.pathname : "/");
  const canonical = `${SITE_URL}${pathname}`;

  return (
    <Helmet>
      <html lang={lang} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {LOCALES.map((l) => (
        <link
          key={l}
          rel="alternate"
          hrefLang={l}
          href={`${SITE_URL}${pathname}?lng=${l}`}
        />
      ))}
      <link rel="alternate" hrefLang="x-default" href={canonical} />
      {/* A static host answers every path with 200; tell crawlers that a
          missing sheet is not a page. */}
      {noindex && <meta name="robots" content="noindex" />}

      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonical} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:image" content={OG_IMAGE} />
      <meta property="og:locale" content={lang} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={OG_IMAGE} />
    </Helmet>
  );
}
